import { c as _c } from "react/compiler-runtime";
import { useContext } from 'react';
import { EventWithData } from '../../../../../typings/Event.interface';
import APIMethodKeys from '../../client/APIMethodKeys';
import { EventListFiltersProps } from '../../typings/EventList.interface';
import { GlobalContext } from '../contexts/GlobalContext';
import useRefreshingList, { RefreshingListResult, RefreshListOptions } from './useRefreshingList';
import { APIFilterOperator } from '../../../../../typings/API.interface';

/**
 * Map UI status values to API EventStatus enum values.
 *
 * UI Components use lowercase, user-friendly values (e.g., 'failed', 'pending')
 * API expects uppercase enum values (e.g., 'FAILED', 'QUEUED')
 *
 * Note: 'pending' maps to 'QUEUED' (the backend status for pending events)
 *
 * Both lowercase and uppercase keys are supported to handle:
 * - UI filter components and URL params (lowercase)
 * - Direct API values or metrics (uppercase)
 */
const status_filters: Record<string, string> = {
  // Lowercase: UI filter components and URL query parameters
  successful: 'SUCCESSFUL',
  failed: 'FAILED',
  pending: 'QUEUED',
  // UI term for QUEUED status
  queued: 'QUEUED',
  paused: 'HOLD',
  // UI term for HOLD status
  scheduled: 'SCHEDULED',
  cancelled: 'CANCELLED',
  // Uppercase: Direct API enum values (pass-through)
  SUCCESSFUL: 'SUCCESSFUL',
  FAILED: 'FAILED',
  QUEUED: 'QUEUED',
  HOLD: 'HOLD',
  SCHEDULED: 'SCHEDULED',
  CANCELLED: 'CANCELLED'
};
type EventListQueryParams = {
  scheduled_attempts?: 'true' | 'false';
};
export type EventListQuery = EventListFiltersProps & EventListQueryParams;
export const formatEventFiltersForAPI = (filters: EventListQuery) => {
  const formatted_filters: {
    [k: string]: any;
  } = {};
  if (typeof filters.date === 'object' && (filters.date.min || filters.date.max)) {
    formatted_filters.created_at = {
      gte: filters.date.min,
      lte: filters.date.max
    };
  }
  if (Array.isArray(filters.response_status)) {
    formatted_filters.response_status = filters.response_status;
  } else if (filters.response_status?.min || filters.response_status?.max) {
    formatted_filters.response_status = {
      ...(filters.response_status.min ? {
        gte: parseInt(filters.response_status.min)
      } : {}),
      ...(filters.response_status.max ? {
        lte: parseInt(filters.response_status.max)
      } : {})
    };
  }
  if (filters.attempts?.min || filters.attempts?.max) {
    formatted_filters.attempts = {
      ...(filters.attempts.min ? {
        gte: parseInt(filters.attempts.min)
      } : {}),
      ...(filters.attempts.max ? {
        lte: parseInt(filters.attempts.max)
      } : {})
    };
  }
  formatted_filters.next = filters.next;
  formatted_filters.prev = filters.prev;
  if (!filters.cli_id) {
    if (filters.cli_user_id !== 'any') {
      formatted_filters.cli_user_id = filters.cli_user_id;
    } else {
      formatted_filters.cli_id = {
        any: true
      };
    }
  } else {
    formatted_filters.cli_id = filters.cli_id;
  }
  if (filters.scheduled_attempts === 'true') {
    filters.next_attempt_at = {
      any: true
    } as APIFilterOperator;
  } else if (filters.scheduled_attempts === 'false') {
    filters.next_attempt_at = '';
  } else {
    filters.next_attempt_at = undefined;
  }
  formatted_filters.error_code = !filters.error_code || filters.error_code.length === 0 ? undefined : filters.error_code;
  formatted_filters.search_term = filters.search_term ?? undefined;
  formatted_filters.headers = filters.request?.headers ?? undefined;
  formatted_filters.body = filters.request?.body ?? undefined;
  formatted_filters.path = filters.request?.path ?? undefined;
  formatted_filters.parsed_query = filters.request?.parsed_query ?? undefined;
  formatted_filters.successful_at = filters.successful_at ?? undefined;
  formatted_filters.next_attempt_at = filters.next_attempt_at;
  formatted_filters.webhook_id = !filters.webhook_id || filters.webhook_id.length === 0 ? undefined : filters.webhook_id;
  formatted_filters.source_id = !filters.source_id || filters.source_id.length === 0 ? undefined : filters.source_id;
  formatted_filters.destination_id = !filters.destination_id || filters.destination_id.length === 0 ? undefined : filters.destination_id;
  formatted_filters.delivery_group = filters.delivery_group;
  formatted_filters.issue_id = !filters.issue_id || filters.issue_id.length === 0 ? undefined : filters.issue_id;
  formatted_filters.bulk_retry_id = !filters.bulk_retry_id || filters.bulk_retry_id.length === 0 ? undefined : filters.bulk_retry_id;
  if (filters.status && filters.status?.length > 0) {
    // Map status values using lookup (already returns uppercase, no need for .toUpperCase())
    formatted_filters.status = filters.status.map(status => status_filters[status]);
  }
  return formatted_filters;
};
export interface EventListResults extends Omit<RefreshingListResult<EventWithData>, 'models'> {
  events: EventWithData[];
}
const useEventList = (filters, options) => {
  const $ = _c(10);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  let t0;
  if ($[0] !== filters) {
    t0 = formatEventFiltersForAPI({
      ...filters
    });
    $[0] = filters;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const formatted_filters = t0;
  let t1;
  if ($[2] !== options) {
    t1 = {
      progressive: true,
      ...options
    };
    $[2] = options;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = useRefreshingList(APIMethodKeys.events.list, HookdeckAPI.events.list, formatted_filters, "created_at", "desc", t1);
  let events;
  let props;
  if ($[4] !== t2) {
    ({
      models: events,
      ...props
    } = t2);
    $[4] = t2;
    $[5] = events;
    $[6] = props;
  } else {
    events = $[5];
    props = $[6];
  }
  let t3;
  if ($[7] !== events || $[8] !== props) {
    t3 = {
      events,
      ...props
    };
    $[7] = events;
    $[8] = props;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
};
export default useEventList;